import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  placement?: 'top' | 'bottom';
  align?: 'left' | 'center' | 'right';
}

export function Tooltip({
  content,
  children,
  placement = 'top',
  align = 'left',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [positioned, setPositioned] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // W2: content가 없으면 포털 마운트 자체를 생략
  if (!content) return <>{children}</>;

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = tooltipRef.current.offsetWidth;
      const tooltipHeight = tooltipRef.current.offsetHeight;
      const viewportWidth = window.innerWidth;
      const MARGIN = 8;

      // I2: placement='top'에서 위에 공간이 부족하면 아래로 fallback
      let top =
        placement === 'bottom'
          ? rect.bottom + 4
          : rect.top - tooltipHeight - 4;
      if (placement === 'top' && top < MARGIN) {
        top = rect.bottom + 4;
      }

      let left: number;
      switch (align) {
        case 'right':
          left = rect.right - tooltipWidth;
          break;
        case 'center':
          left = rect.left + rect.width / 2 - tooltipWidth / 2;
          break;
        case 'left':
        default:
          left = rect.left;
          break;
      }

      if (left + tooltipWidth > viewportWidth - MARGIN) {
        left = viewportWidth - tooltipWidth - MARGIN;
      }
      if (left < MARGIN) {
        left = MARGIN;
      }

      setPosition({ top, left });
      // W1: 위치 계산 완료 후 visible 처리
      setPositioned(true);
    }
  }, [isVisible, placement, align]);

  const handleMouseLeave = () => {
    setIsVisible(false);
    setPositioned(false);
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>

      {isVisible &&
        createPortal(
          <div
            ref={tooltipRef}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              // W1: 위치 계산 전 첫 프레임에 0,0에 렌더되는 플래시 방지
              visibility: positioned ? 'visible' : 'hidden',
            }}
            className="z-[9999] bg-gray-800 text-white text-xs rounded-card-sm py-2 px-3 max-w-xs shadow-lg"
            onMouseEnter={() => setIsVisible(true)}
            onMouseLeave={handleMouseLeave}
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}

export default Tooltip;
